import { Button } from "components/ui/Button";
import { Link as UiLink } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import type { TalentFilters, TalentListItem } from "hooks/use-talents-api";
import { useCrmPermission } from "hooks/useCrmPermission";
import type { RestApiError } from "lib/rest-client";
import type React from "react";
import { Link } from "react-router-dom";
import {
  getAffiliationLabel,
  getEmploymentTypeLabel,
  getWorkStatusBadge,
} from "routes/crm/utils";

type TalentListPageProps = {
  talents: TalentListItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  filters: TalentFilters;
  onSearchChange: (search: string) => void;
  onAffiliationChange: (affiliation: string) => void;
  onEmploymentTypeChange: (employmentType: string) => void;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
};

/**
 * タレント一覧ページのプレゼンテーションコンポーネント
 * @param {object} props プロパティ
 * @param {Array} props.talents タレント一覧
 * @param {number} props.total 総件数
 * @param {boolean} props.loading ローディング状態
 * @param {object} props.error エラー
 * @param {object} props.filters フィルター状態
 * @param {Function} props.onSearchChange 検索変更ハンドラ
 * @param {Function} props.onAffiliationChange 所属変更ハンドラ
 * @param {Function} props.onEmploymentTypeChange 雇用形態変更ハンドラ
 * @param {Function} props.onPageChange ページ変更ハンドラ
 * @param {number} props.itemsPerPage 1ページあたりの件数
 * @return {React.ReactElement} タレント一覧ページ
 */
export const TalentListPage: React.FC<TalentListPageProps> = ({
  talents,
  total,
  loading,
  error,
  filters,
  onSearchChange,
  onAffiliationChange,
  onEmploymentTypeChange,
  onPageChange,
  itemsPerPage,
}) => {
  const { canEdit } = useCrmPermission();
  const currentPage = filters.page || 1;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">タレント管理</h1>
        {canEdit && (
          <Button asChild size="sm">
            <Link to="/crm/talents/new">新規登録</Link>
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="名前・スキルで検索..."
          className="input input-bordered input-sm w-64"
          value={filters.search || ""}
          onChange={e => onSearchChange(e.target.value)}
        />
        <select
          className="select select-bordered select-sm"
          value={filters.affiliation || ""}
          onChange={e => onAffiliationChange(e.target.value)}
        >
          <option value="">全て</option>
          <option value="proprietary">自社</option>
          <option value="partner">パートナー</option>
        </select>
        <select
          className="select select-bordered select-sm"
          value={filters.employmentType || ""}
          onChange={e => onEmploymentTypeChange(e.target.value)}
        >
          <option value="">全て</option>
          <option value="full_time">正社員</option>
          <option value="freelancer">フリーランス</option>
          <option value="contract">契約社員</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error mb-4">
          <span>データの取得に失敗しました: {error.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <Loading size="lg" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <Table.Root density="compact" zebra stickyHeader>
              <Table.Head sticky>
                <tr>
                  <Table.HeaderCell>名前</Table.HeaderCell>
                  <Table.HeaderCell>所属</Table.HeaderCell>
                  <Table.HeaderCell>雇用形態</Table.HeaderCell>
                  <Table.HeaderCell>スキル</Table.HeaderCell>
                  <Table.HeaderCell>稼働状態</Table.HeaderCell>
                  <Table.HeaderCell>希望単価</Table.HeaderCell>
                  <Table.HeaderCell>担当</Table.HeaderCell>
                  <Table.HeaderCell>操作</Table.HeaderCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {talents.map(talent => {
                  const status = getWorkStatusBadge(talent.workStatus ?? "");
                  return (
                    <Table.Row key={talent.id}>
                      <Table.Cell>
                        <UiLink to={`/crm/talents/${talent.id}`}>
                          {talent.name}
                        </UiLink>
                      </Table.Cell>
                      <Table.Cell>
                        {getAffiliationLabel(talent.affiliation ?? "")}
                      </Table.Cell>
                      <Table.Cell>
                        {getEmploymentTypeLabel(talent.employmentType ?? "")}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          {talent.skills.slice(0, 3).map(skill => (
                            <span
                              key={skill}
                              className="badge badge-outline badge-sm"
                            >
                              {skill}
                            </span>
                          ))}
                          {talent.skills.length > 3 && (
                            <StatusBadge tone="soft-neutral">
                              +{talent.skills.length - 3}
                            </StatusBadge>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <StatusBadge tone={status.tone}>
                          {status.label}
                        </StatusBadge>
                      </Table.Cell>
                      <Table.Cell>
                        {talent.desiredReward != null
                          ? `${talent.desiredReward.toLocaleString()}円`
                          : "-"}
                      </Table.Cell>
                      <Table.Cell>{talent.assigneeName || "-"}</Table.Cell>
                      <Table.Cell>
                        <Button asChild variant="ghost" size="xs">
                          <Link to={`/crm/talents/${talent.id}`}>詳細</Link>
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {talents.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-base-content/60"
                    >
                      タレントが見つかりません
                    </td>
                  </tr>
                )}
              </Table.Body>
            </Table.Root>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="join">
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  isJoinItem
                  disabled={currentPage <= 1}
                  onClick={() => onPageChange(currentPage - 1)}
                >
                  &laquo;
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  page => (
                    <Button
                      type="button"
                      key={page}
                      variant="neutral"
                      size="sm"
                      isJoinItem
                      isActive={page === currentPage}
                      onClick={() => onPageChange(page)}
                    >
                      {page}
                    </Button>
                  ),
                )}
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  isJoinItem
                  disabled={currentPage >= totalPages}
                  onClick={() => onPageChange(currentPage + 1)}
                >
                  &raquo;
                </Button>
              </div>
            </div>
          )}

          <div className="text-sm text-base-content/60 mt-2 text-center">
            {total}件中 {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, total)}件を表示
          </div>
        </>
      )}
    </div>
  );
};
